
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * LanguageInfo struct
 */
class LanguageInfo {
  /**
   * Initialize struct
   * @param {!string=} code
   * @param {!string=} description
   * @constructor
   */
  constructor (code = '', description = '') {
    this.Code = code
    this.Description = description
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LanguageInfo}
   * @param {!LanguageInfo} other Other struct
   * @returns {!LanguageInfo} This struct
   */
  copy (other) {
    if (other.Code != null) {
      this.Code = other.Code
    } else {
      this.Code = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LanguageInfo}
   * @returns {!LanguageInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LanguageInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LanguageInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LanguageInfo}
   * @param {!LanguageInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LanguageInfo)) {
      throw new TypeError('Instance of LanguageInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LanguageInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Code: ((this.Code != null) ? this.Code : null),
      Description: ((this.Description != null) ? this.Description : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LanguageInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LanguageInfo} other Object value
   * @returns {!LanguageInfo} Created struct
   */
  static fromObject (other) {
    return new LanguageInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LanguageInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LanguageInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LanguageInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5559
  }
}

export { LanguageInfo };

/**
 * Fast Binary Encoding LanguageInfo field model
 */
class FieldModelLanguageInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Code = new fbe.FieldModelString(buffer, 4 + 4)
    this.Description = new fbe.FieldModelString(buffer, this.Code.fbeOffset + this.Code.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Code.fbeSize + this.Description.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Code.fbeExtra + this.Description.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLanguageInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5559
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLanguageInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLanguageInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Code.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Code.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Code.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLanguageInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLanguageInfo}
   * @param {!LanguageInfo} fbeValue Default value, defaults is new LanguageInfo()
   * @returns {!LanguageInfo} LanguageInfo value
   */
  get (fbeValue = new LanguageInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLanguageInfo}
   * @param {!LanguageInfo} fbeValue LanguageInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Code.fbeSize) <= fbeStructSize) {
      fbeValue.Code = this.Code.get()
    } else {
      fbeValue.Code = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Code.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLanguageInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLanguageInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLanguageInfo}
   * @param {!LanguageInfo} fbeValue LanguageInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLanguageInfo}
   * @param {LanguageInfo} fbeValue LanguageInfo value
   */
  setFields (fbeValue) {
    this.Code.set(fbeValue.Code)
    this.Description.set(fbeValue.Description)
  }
}

export { FieldModelLanguageInfo };

/**
 * Fast Binary Encoding LanguageInfo model
 */
class LanguageInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLanguageInfo(this.buffer, 4)
  }

  /**
   * Get the LanguageInfo model
   * @this {!LanguageInfoModel}
   * @returns {!FieldModelLanguageInfo} model LanguageInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LanguageInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LanguageInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LanguageInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LanguageInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLanguageInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LanguageInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LanguageInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LanguageInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LanguageInfoModel}
   * @param {!LanguageInfo} value LanguageInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LanguageInfoModel}
   * @param {!LanguageInfo} value LanguageInfo value, defaults is new LanguageInfo()
   * @return {!object} Deserialized LanguageInfo value and its size
   */
  deserialize (value = new LanguageInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LanguageInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LanguageInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LanguageInfoModel}
   * @param {!number} prev Previous LanguageInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LanguageInfoModel };
